import { Button } from "@bcc/components/ui/button";
import {
  AlertTriangle,
  DollarSign,
  LucideIcon,
  ShoppingCart,
  Star,
  Tag,
  Trophy,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  setAlertsInsightsTestId,
  setAlertsInsightsTitleTestId,
} from "./alerts-insights.utils";

export interface AlertData {
  id: number;
  type: "error" | "warning" | "success" | "info";
  icon: string;
  title: string;
  message: string;
  primaryAction: string;
  primaryLink: string;
  secondaryAction: string;
  bgColor: string;
  iconColor: string;
  primaryButtonColor: string;
}

interface AlertsInsightsProps {
  data: AlertData[];
  testId?: string;
}

const iconMap: Record<string, LucideIcon> = {
  AlertTriangle,
  Tag,
  Trophy,
  DollarSign,
  ShoppingCart,
  Star,
};

interface AlertCardProps {
  alert: AlertData;
  index: number;
}

function AlertCard({ alert, index }: AlertCardProps) {
  const IconComponent = iconMap[alert.icon] || AlertTriangle;

  return (
    <div
      className={`rounded-lg border ${alert.bgColor} p-4 transition-all hover:shadow-md`}
      data-testid={setAlertsInsightsTestId(`card-${index}`)}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${alert.iconColor}`}
        >
          <IconComponent className="h-5 w-5" />
        </div>
        <div className="flex-1 space-y-1">
          <h3
            className="font-medium text-sm"
            data-testid={setAlertsInsightsTestId(`title-${index}`)}
          >
            {alert.title}
          </h3>
          <p className="text-sm text-muted-foreground">{alert.message}</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Link to={alert.primaryLink} className="flex-1">
          <Button
            size="sm"
            className={`w-full ${alert.primaryButtonColor}`}
            data-testid={setAlertsInsightsTestId(`action-${index}`)}
          >
            {alert.primaryAction}
          </Button>
        </Link>
        <Button
          size="sm"
          variant="ghost"
          className="bg-transparent hover:bg-black/5"
        >
          {alert.secondaryAction}
        </Button>
      </div>
    </div>
  );
}

export function AlertsInsights({ data, testId }: AlertsInsightsProps) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId ?? setAlertsInsightsTestId("container")}
      className="space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-lg font-semibold"
            data-testid={setAlertsInsightsTitleTestId()}
          >
            {t("dashboardComponents.alertsInsights.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("dashboardComponents.alertsInsights.description")}
          </p>
        </div>
        <Link
          to="/alerts"
          className="text-sm font-medium text-primary hover:underline"
          data-testid={setAlertsInsightsTestId("view-all-link")}
        >
          {t("dashboardComponents.alertsInsights.viewAll")}
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.map((alert, index) => (
          <AlertCard key={alert.id} alert={alert} index={index} />
        ))}
      </div>
    </div>
  );
}
